// Route guard. Wrap any role-gated subtree.
//
// Behavior:
//   - Loading: render small spinner so we don't flash the public landing.
//   - Not signed in: redirect to /login?returnTo=<current path>.
//   - Signed in but wrong role: redirect to the user's own default surface.
//
// Roles allowed via the `allow` prop. Pass ['admin', 'super_admin'] for
// admin-only routes, etc.

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { Me } from '../../lib/api';

type Role = Me['role'];

interface Props {
  allow: Role[];
  children: React.ReactNode;
}

const DEFAULT_LANDING: Record<Role, string> = {
  customer:    '/customer',
  recipient:   '/recipient',
  merchant:    '/merchant',
  admin:       '/admin',
  super_admin: '/admin',
};

export function RequireAuth({ allow, children }: Props) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[12px] text-[var(--color-muted)]">
        Loading…
      </div>
    );
  }

  if (!user) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (!allow.includes(user.role)) {
    return <Navigate to={DEFAULT_LANDING[user.role]} replace />;
  }

  return <>{children}</>;
}
